import React, { useCallback, useMemo } from 'react';
import FormInput from './components/form-input/FormInput/FormInput';
import ToggleInput from './components/form-input/ToggleInput/ToggleInput';
import FormInputDisabled from './components/FormDisabledInfo/FormDisabledInfo';
import FormInputDropzone from './components/form-image/FormDropzone';
import FormCheckboxList from './components/form-checkbox/FormCheckboxList/FormCheckboxList';
import FormCheckbox from './components/form-checkbox/FormCheckbox/FormCheckbox';
import FormCheckboxSwitcher from './components/form-checkbox/FormCheckboxSwitcher/FormCheckboxSwitcher';
import FormSponsorWidget from './components/form-sponsor-widget/FormSponsorWidget';
import FormDate from './components/form-date/FormDate/FormDate';
import FormChangePasswordWidget from './components/form-change-data-in-modal/FormChangeDataInModal';
import FormRadioSwitcher from './components/form-radio/FormRadioSwitcher/FormRadioSwitcher';
import FormSelectLocation from './components/form-dropdown/FormSelectLocation/FormSelectLocation';
import FormRange from './components/form-input/FormRangeValues/FormRangeValues';
import FormRadioCircles from './components/form-radio/FormRadioCircles/FormRadioCircles';
import FormTextArea from './components/form-textarea/FormInputArea';
import FormProductPrices from './components/form-product-prices/FormProductPrices/FormProductPrices';
import FormProductPricesCustom from './components/form-product-prices/FormProductPrices/FormProductPricesCustom';
import Editor from './components/form-editor/FormEditor';
import FormColorPicker from './components/form-color/FormColorPicker';
import FormMap from './components/form-map/FormMap';
import SelectDataWidget from './components/select-data-widget/SelectDataWidget/SelectDataWidget';
import FormDateRange from './components/form-date/FormDateRange/FormDateRange';
import FormCaptcha from './components/FormCaptcha/FormCaptcha';
import FormIcons from './components/form-icons/FormIcons/FormIcons';
import FormRate from './components/form-rate/FormRate';
import FormDropdown from './components/form-dropdown/FormDropdown/FormDropdown';
import FormSelectUsers from './components/form-select-users/FormSelectUsers';
import SelectDataGrid from './components/select-data-grid/SelectDataGrid/SelectDataGrid';
import FormInputDropzoneOfficeCustom from './components/form-image-office-custom/FormDropzone';
import ChooseProduct from './components/choose-product/ChooseProduct';
import ChoosePack from './components/choose-pack/ChoosePack';
import SelectUserStatement from './components/user-statement/SelectDataWidget/SelectDataWidget';
import YoutubePlayer from '../youtubePlayer/YoutubePlayer';
import SelectProducts from './components/select-products/SelectProducts';

const FormSwitcher = ({
    data,
    formName,
    funcs,
    formStyles,
    formData,
    fieldsErr,
    customData,
    sendFormData,
    message,
    setFormData,
    captchaData
}) => {
    const zIndexes = useMemo(() => {
        return data.reduce((acc, item, index) => {
            return { ...acc, [item.attribute]: data.length - index + 1 };
        }, {});
    }, [data]);
    const getPropsData = useCallback(
        (item, idx) => {
            const { attribute, disabled, compStyles, url } = item;
            return {
                ...item,
                zIndex: zIndexes[attribute],
                handlers: funcs?.[attribute] || {},
                compStyle: compStyles && compStyles.constructor === Object ? compStyles : formStyles[attribute] || {},
                setFormData: setFormData,
                errors: [
                    ...(fieldsErr?.[item.attribute] || []),
                    ...(item?.additionalErrorsAttributes
                        ? item?.additionalErrorsAttributes?.filter((attr) => fieldsErr?.[attr])?.map((attr) => fieldsErr?.[attr])
                        : [])
                ],
                hint: item?.hint,
                disabled,
                customData: customData?.[attribute],
                key: `${attribute}${formName}${idx}`,
                message,
                routePage: item?.routePage ? item.routePage : null,
                fieldsErr: fieldsErr,
                url: url || false
            };
        },
        [formStyles, zIndexes, customData, fieldsErr, formName, funcs, message, setFormData]
    );
    return (
        <>
            {data.map((item, idx) => {
                const propsData = getPropsData(item, idx);
                switch (item.type) {
                    case 'phone-widget':
                    case 'password':
                    case 'input':
                        return <FormInput {...propsData} sender={sendFormData} />;
                    case 'toggle-input':
                        return <ToggleInput {...propsData} />;
                    case 'rating':
                        return <FormRate {...propsData} />;
                    case 'dropdown':
                    case 'select':
                        return <FormDropdown {...propsData} />;
                    case 'checkbox-list':
                        return <FormCheckboxList {...propsData} />;
                    case 'checkbox':
                        return <FormCheckbox {...propsData} />;
                    case 'checkbox-switcher':
                        return <FormCheckboxSwitcher {...propsData} />;
                    case 'radio-list':
                        return <FormRadioCircles {...propsData} />;
                    case 'sponsor-widget':
                        return <FormSponsorWidget {...propsData} />;
                    case 'select-data-widget':
                    case 'admin-sponsor-widget':
                        return <SelectDataWidget {...propsData} />;
                    case 'range-date-widget':
                        return <FormDateRange {...propsData} />;
                    case 'date-widget':
                        return <FormDate {...propsData} />;
                    case 'radio-switcher':
                    case 'sex-widget':
                        return <FormRadioSwitcher {...propsData} />;
                    case 'location_widget':
                        return <FormSelectLocation {...propsData} formData={formData} />;
                    case 'dropdown-multiselect':
                        return <FormDropdown {...propsData} multiple={true} />;
                    case 'image':
                    case 'dropzone':
                        return <FormInputDropzone {...propsData} />;
                    case 'image-office-custom':
                        return <FormInputDropzoneOfficeCustom {...propsData} />;
                    case 'info-field':
                        return <FormInputDisabled {...propsData} />;
                    case 'range-values':
                        return <FormRange {...propsData} />;
                    case 'password-change-widget':
                    case 'email-change-widget':
                        return <FormChangePasswordWidget {...propsData} />;
                    case 'textarea':
                        return <FormTextArea {...propsData} sender={sendFormData} />;
                    case 'editor':
                        return <Editor {...propsData} />;
                    case 'product-prices':
                        return <FormProductPrices {...propsData} errors={fieldsErr} formData={formData} />;
                    case 'product-prices-custom':
                        return <FormProductPricesCustom {...propsData} errors={fieldsErr} formData={formData} />;
                    case 'color-picker':
                        return <FormColorPicker {...propsData} />;
                    case 'maps':
                        return <FormMap {...propsData} />;
                    case 'icon':
                        return <FormIcons {...propsData} />;
                    case 'select-users':
                        return <FormSelectUsers {...propsData} />;
                    case 'select-data-grid':
                        return <SelectDataGrid {...propsData} />;
                    case 'choose-product':
                        return <ChooseProduct {...propsData} />;
                    case 'choose-pack':
                        return <ChoosePack {...propsData} />;
                    case 'user-statement':
                        return <SelectUserStatement {...propsData} />;
                    case 'youtube-player':
                        return <YoutubePlayer {...propsData} />;
                    case 'select-products':
                        return <SelectProducts {...propsData} />;
                    default:
                        return null;
                }
            })}
            {captchaData ? (
                <FormCaptcha
                    align={captchaData?.align}
                    alias={captchaData?.alias}
                    attempts={captchaData?.attempts}
                    fieldsErr={fieldsErr}
                    formStyles={formStyles}
                    setFormData={setFormData}
                />
            ) : null}
        </>
    );
};
export default FormSwitcher;
